// Re-export types from modular system
export type { ToolPageType, ToolExampleType, HowItWorksStep, Benefit, FaqItem, ToolDefinition } from './tools/types';
import type { ToolDefinition } from './tools/types';
import { manualTools } from './tools/manualTools';
import { generatePhase1Tools } from './tools/generators';
import { assignRelatedSlugs } from './tools/relatedSlugs';

// ─── Metadata enrichment map for existing manual tools ──────────────────────
// Adds explicit targetSize / sourceFormat / targetFormat / platform / dimensions
// so ToolPage.tsx can read metadata directly instead of parsing slugs.
const metadataMap: Record<string, Partial<ToolDefinition>> = {
  // Target-size tools
  'reducir-imagen-200kb': { targetSize: '200KB' },
  'reducir-imagen-100kb': { targetSize: '100KB' },
  'reducir-imagen-50kb': { targetSize: '50KB' },
  'reducir-imagen-20kb': { targetSize: '20KB' },
  'reducir-imagen-10kb': { targetSize: '10KB' },
  'reducir-imagen-500kb': { targetSize: '500KB' },
  'reducir-imagen-300kb': { targetSize: '300KB' },
  'reducir-imagen-150kb': { targetSize: '150KB' },
  'reducir-imagen-250kb': { targetSize: '250KB' },
  'reducir-imagen-25kb': { targetSize: '25KB' },
  'reducir-imagen-2mb': { targetSize: '2MB' },
  'reducir-imagen-5mb': { targetSize: '5MB' },
  'reducir-foto-200kb': { targetSize: '200KB' },
  'reducir-foto-100kb': { targetSize: '100KB' },
  // Conversion tools
  'convertir-webp-jpg': { sourceFormat: 'WebP', targetFormat: 'JPG' },
  'convertir-jpg-webp': { sourceFormat: 'JPG', targetFormat: 'WebP' },
  'convertir-png-jpg': { sourceFormat: 'PNG', targetFormat: 'JPG' },
  'convertir-jpg-png': { sourceFormat: 'JPG', targetFormat: 'PNG' },
  'convertir-webp-png': { sourceFormat: 'WebP', targetFormat: 'PNG' },
  'convertir-png-webp': { sourceFormat: 'PNG', targetFormat: 'WebP' },
  'convertir-heic-jpg': { sourceFormat: 'HEIC', targetFormat: 'JPG' },
  'convertir-heic-png': { sourceFormat: 'HEIC', targetFormat: 'PNG' },
  'convertir-bmp-jpg': { sourceFormat: 'BMP', targetFormat: 'JPG' },
  'convertir-bmp-png': { sourceFormat: 'BMP', targetFormat: 'PNG' },
  'convertir-gif-jpg': { sourceFormat: 'GIF', targetFormat: 'JPG' },
  'convertir-gif-png': { sourceFormat: 'GIF', targetFormat: 'PNG' },
  'convertir-svg-png': { sourceFormat: 'SVG', targetFormat: 'PNG' },
  'convertir-tiff-jpg': { sourceFormat: 'TIFF', targetFormat: 'JPG' },
  // Resize tools
  'redimensionar-imagen-1080x1080': { dimensions: '1080x1080' },
  'redimensionar-imagen-instagram': { platform: 'Instagram' },
  'redimensionar-imagen-facebook': { platform: 'Facebook' },
  'redimensionar-imagen-twitter': { platform: 'Twitter' },
  'redimensionar-imagen-linkedin': { platform: 'LinkedIn' },
  'redimensionar-imagen-youtube': { platform: 'YouTube' },
  'redimensionar-imagen-pinterest': { platform: 'Pinterest' },
  'redimensionar-imagen-tiktok': { platform: 'TikTok' },
  'redimensionar-imagen-1920x1080': { dimensions: '1920x1080' },
  'redimensionar-imagen-800x600': { dimensions: '800x600' },
  'redimensionar-imagen-pasaporte': { platform: 'Pasaporte', dimensions: '413x531' },
  'redimensionar-imagen-dni': { platform: 'DNI' },
  'redimensionar-imagen-carnet': { platform: 'Carnet' },
  'redimensionar-foto-pasaporte': { platform: 'Pasaporte', dimensions: '413x531' },
  'redimensionar-foto-carnet': { platform: 'Carnet' },
  'redimensionar-imagen-para-web': { platform: 'Web' },
};

// ─── Enrich manual tools with explicit metadata ─────────────────────────────
const enrichedManualTools: ToolDefinition[] = manualTools.map(tool => {
  const meta = metadataMap[tool.slug];
  return meta ? { ...tool, ...meta } : tool;
});

// ─── Merge generated tools (Phase 1) ────────────────────────────────────────
const manualSlugs = new Set(enrichedManualTools.map(t => t.slug));
const generatedTools = generatePhase1Tools().filter(t => !manualSlugs.has(t.slug));

// Assign related slugs to generated tools (manual tools keep their existing ones)
export const tools: ToolDefinition[] = assignRelatedSlugs([
  ...enrichedManualTools,
  ...generatedTools,
]);

// Helper functions
export const getToolBySlug = (slug: string): ToolDefinition | undefined =>
  tools.find(t => t.slug === slug);

export const getToolsByPageType = (pageType: ToolDefinition['pageType']): ToolDefinition[] =>
  tools.filter(t => t.pageType === pageType);

export const getAllTools = (): ToolDefinition[] => tools;
